import React, { type ReactElement } from 'react';
import { toast } from 'react-toastify';
import ConfirmModal, { ConfirmModalActionPair } from 'atom-components/confirm-modal';
import FullName from 'feature-components/full-name';
import { refreshMarkingTables } from 'pages/marking/helpers/react-query.helpers';
import type { PartialOrganisationUserWithPartialUser } from 'services/assist-api/entities/customer-user/types';
import { useDeleteTestInstanceResponseMarker } from 'services/assist-api/entities/test-instance-response-marker/hooks';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onDeallocate?: () => void;
  responseId: number;
  allocatedMarkerId: number;
  allocatedOrganisationUser: PartialOrganisationUserWithPartialUser | undefined;
};

export default function ConfirmDeallocateMarkerModal({
  isOpen,
  onClose,
  onDeallocate,
  responseId,
  allocatedMarkerId,
  allocatedOrganisationUser
}: Props): ReactElement {
  const deleteTestInstanceResponseMarker = useDeleteTestInstanceResponseMarker({
    onSuccess: () => {
      toast.success('Marker has been deallocated.');
      refreshMarkingTables();
      if (onDeallocate) {
        onDeallocate();
      }
    },
    onError: (error) => {
      toast.error(`Error deallocating marker: ${error.message}`);
    },
    onSettled: () => {
      onClose();
    }
  });

  return (
    <ConfirmModal
      isOpen={isOpen}
      title="Confirm Deallocate Marker"
      handleClickCancel={(): void => onClose()}
      handleClickOK={(): void => {
        deleteTestInstanceResponseMarker.mutate(allocatedMarkerId);
      }}
      actionPair={ConfirmModalActionPair.Custom}
      buttonTitleOK="Deallocate Marker"
      buttonTitleCancel="Cancel"
    >
      Are you sure that you want to deallocate{' '}
      <FullName organisationUser={allocatedOrganisationUser} bringAttentionTo /> from{' '}
      <b>response #{responseId}</b>?
    </ConfirmModal>
  );
}
